import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  API_BASE,
  subscribeToEvents,
  getOutput,
  getStatus,
  getHistoryOutput,
  transformEventJson,
  saveAsTemplate,
  startEdit,
} from '@/api/client';
import type { SSEEvent, PipelineOutput } from '@/api/client';

/**
 * Unified preview page — handles both live pipeline runs and saved history.
 *
 * Provide `runId` for a live run (SSE streaming + step log).
 * Provide `folderName` for a saved run (load from disk).
 * Both show the same preview iframe, toolbar, and edit capabilities.
 */

interface PreviewPageProps {
  /** Live pipeline run ID — subscribes to SSE for progress. */
  readonly runId?: string;
  /** Saved output folder — loads directly from disk. */
  readonly folderName?: string;
  readonly onBack: () => void;
  /** Called when an edit starts a new pipeline run. */
  readonly onRunStarted: (runId: string) => void;
}

interface StepEntry {
  step: string;
  status: 'running' | 'complete' | 'error';
  durationMs?: number;
  error?: string;
}

export function PreviewPage({ runId, folderName, onBack, onRunStarted }: PreviewPageProps) {
  const isLiveRun = !!runId;
  const label = runId ? `Run ${runId.slice(0, 8)}...` : (folderName ?? '');

  // Pipeline state (only used for live runs)
  const [steps, setSteps] = useState<StepEntry[]>([]);
  const [pipelineStatus, setPipelineStatus] = useState<'loading' | 'running' | 'completed' | 'failed'>(
    isLiveRun ? 'loading' : 'completed',
  );
  const [stepsExpanded, setStepsExpanded] = useState(isLiveRun);

  // Output state (shared)
  const [output, setOutput] = useState<PipelineOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [contentOverride, setContentOverride] = useState<string | null>(null);
  const [swapStatus, setSwapStatus] = useState<'idle' | 'transforming'>('idle');

  // Publish state
  const [publishStatus, setPublishStatus] = useState<'idle' | 'done'>('idle');
  const [publishUrl, setPublishUrl] = useState<string | null>(null);

  // Template state
  const [templateStatus, setTemplateStatus] = useState<'idle' | 'naming' | 'saving' | 'saved'>('idle');
  const [templateName, setTemplateName] = useState('');

  // Edit state
  const [editInstruction, setEditInstruction] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const initializedRef = useRef(false);
  const contentInputRef = useRef<HTMLInputElement>(null);

  // Folder name for this site — from history prop or from a completed live run
  const effectiveFolderName = folderName ?? output?.outputDir ?? null;

  // ---------------------------------------------------------------------------
  // Data loading — either SSE subscription or direct fetch
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (isLiveRun) {
      loadLiveRun(runId);
    } else if (folderName) {
      getHistoryOutput(folderName)
        .then(setOutput)
        .catch(err => setError(err.message));
    }
  }, [runId, folderName, isLiveRun]);

  function loadLiveRun(id: string) {
    getStatus(id)
      .then(status => {
        if ('error' in status) {
          setError(status.error as string);
          setPipelineStatus('failed');
          return;
        }

        if (status.steps?.length) {
          setSteps(
            status.steps.map(s => ({
              step: s.step,
              status: s.success ? ('complete' as const) : ('error' as const),
              durationMs: s.durationMs,
              error: s.error ?? undefined,
            })),
          );
        }

        if (status.status === 'completed') {
          setPipelineStatus('completed');
          setStepsExpanded(false);
          getOutput(id)
            .then(setOutput)
            .catch(() => setError('Failed to fetch output'));
          return;
        }

        if (status.status === 'failed') {
          setPipelineStatus('failed');
          return;
        }

        setPipelineStatus('running');
        subscribeToSSE(id);
      })
      .catch(() => {
        setPipelineStatus('running');
        subscribeToSSE(id);
      });
  }

  function subscribeToSSE(id: string) {
    subscribeToEvents(
      id,
      (event: SSEEvent) => {
        if (event.type === 'step-start') {
          setSteps(prev => [...prev, { step: event.data['step'] as string, status: 'running' }]);
        }
        if (event.type === 'step-complete') {
          setSteps(prev =>
            prev.map(s =>
              s.step === event.data['step']
                ? { ...s, status: 'complete', durationMs: event.data['durationMs'] as number }
                : s,
            ),
          );
        }
        if (event.type === 'step-error') {
          setSteps(prev =>
            prev.map(s =>
              s.step === event.data['step'] ? { ...s, status: 'error', error: event.data['error'] as string } : s,
            ),
          );
        }
        if (event.type === 'pipeline-complete') {
          setPipelineStatus('completed');
          setStepsExpanded(false);
        }
        if (event.type === 'pipeline-error') {
          setPipelineStatus('failed');
          setError(event.data['error'] as string);
        }
      },
      () => {
        getOutput(id)
          .then(setOutput)
          .catch(() => setError('Failed to fetch output'));
      },
      err => {
        setPipelineStatus('failed');
        setError(err.message);
      },
    );
  }

  // ---------------------------------------------------------------------------
  // Content swap
  // ---------------------------------------------------------------------------

  const handleContentUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async event => {
      const text = event.target?.result;
      if (typeof text !== 'string') return;

      setSwapStatus('transforming');
      try {
        const parsed = JSON.parse(text);
        const contentJson = await transformEventJson(parsed);
        setContentOverride(contentJson);
      } catch (err) {
        setError(`Content swap failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setSwapStatus('idle');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  // ---------------------------------------------------------------------------
  // Download
  // ---------------------------------------------------------------------------

  const handleDownload = useCallback(() => {
    if (!output) return;
    const prefix = folderName ?? runId ?? 'site';
    const downloadFile = (content: string, filename: string, mimeType: string) => {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };
    downloadFile(output.html, `${prefix}_index.html`, 'text/html');
    downloadFile(contentOverride ?? output.contentJson, `${prefix}_content.json`, 'application/json');
  }, [output, contentOverride, folderName, runId]);

  // ---------------------------------------------------------------------------
  // Publish
  // ---------------------------------------------------------------------------

  const handleSaveTemplate = useCallback(async () => {
    if (!folderName || !templateName.trim()) return;
    setTemplateStatus('saving');
    try {
      await saveAsTemplate(folderName, { name: templateName.trim() });
      setTemplateStatus('saved');
    } catch (err) {
      setError(`Save template failed: ${err instanceof Error ? err.message : String(err)}`);
      setTemplateStatus('idle');
    }
  }, [folderName, templateName]);

  const handlePublish = useCallback(() => {
    const name = effectiveFolderName;
    if (!name) return;
    const url = `${window.location.origin}/api/render/${encodeURIComponent(name)}`;
    navigator.clipboard.writeText(url);
    setPublishUrl(url);
    setPublishStatus('done');
  }, [effectiveFolderName]);

  // ---------------------------------------------------------------------------
  // Edit
  // ---------------------------------------------------------------------------

  const handleEdit = useCallback(async () => {
    if (!editInstruction.trim() || !folderName) return;
    setEditSubmitting(true);
    setError(null);
    try {
      const { runId: editRunId } = await startEdit(folderName, editInstruction.trim());
      setEditInstruction('');
      onRunStarted(editRunId);
    } catch (err) {
      setError(`Edit failed: ${err instanceof Error ? err.message : String(err)}`);
      setEditSubmitting(false);
    }
  }, [editInstruction, folderName, onRunStarted]);

  // ---------------------------------------------------------------------------
  // Preview HTML
  // ---------------------------------------------------------------------------

  const activeContentJson = contentOverride ?? output?.contentJson;

  // Use the SSR render endpoint when we have a folder name (history view or completed run).
  const renderUrl = useMemo(() => {
    if (!effectiveFolderName || contentOverride) return null;
    return `${API_BASE}/render/${encodeURIComponent(effectiveFolderName)}`;
  }, [effectiveFolderName, contentOverride]);

  // For live runs or content overrides, use srcDoc with injected content.
  const previewHtml = useMemo(() => {
    // If we can use the render endpoint, prefer that
    if (renderUrl && !contentOverride) return null;
    if (!output || !activeContentJson) return null;

    // Inject content data as a global variable — the runtime checks for this before fetching.
    const contentInjection = `<script>window.__EVENT_WEBSITE_CONTENT__ = ${activeContentJson};</script>`;
    let html = output.html.replace('<div id="root">', `${contentInjection}\n<div id="root">`);

    // Legacy support: replace inline fetch for templates that don't use the external runtime
    html = html.replace(
      "fetch('./content.json')",
      `Promise.resolve({ json: () => Promise.resolve(${activeContentJson}) })`,
    );

    const scrollFixScript = `<script>
document.addEventListener('click', function(e) {
  var anchor = e.target.closest('a[href^="#"]');
  if (!anchor) return;
  e.preventDefault();
  var id = anchor.getAttribute('href').slice(1);
  var target = document.getElementById(id);
  if (target) target.scrollIntoView({ behavior: 'smooth' });
});
</script>`;
    html = html.replace('</body>', scrollFixScript + '</body>');

    return html;
  }, [output, activeContentJson, renderUrl, contentOverride]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <button
          style={styles.toolbarButton}
          onClick={onBack}
        >
          Back
        </button>
        <span style={styles.toolbarTitle}>
          {label}
          {contentOverride ? ' (custom content)' : ''}
          {isLiveRun ? ` — ${pipelineStatus}` : ''}
        </span>
        <div style={styles.toolbarActions}>
          {output && (
            <>
              <button
                style={styles.toolbarButton}
                onClick={() => contentInputRef.current?.click()}
                disabled={swapStatus === 'transforming'}
              >
                {swapStatus === 'transforming' ? 'Transforming...' : 'Swap Content'}
              </button>
              <input
                ref={contentInputRef}
                type="file"
                accept=".json"
                onChange={handleContentUpload}
                style={styles.hiddenInput}
              />
              {contentOverride && (
                <button
                  style={styles.toolbarButton}
                  onClick={() => setContentOverride(null)}
                >
                  Reset Content
                </button>
              )}
              <button
                style={styles.downloadButton}
                onClick={handleDownload}
              >
                Download Site
              </button>
              {folderName && (
                <>
                  <button
                    style={publishStatus === 'done' ? styles.publishedButton : styles.publishButton}
                    onClick={
                      publishStatus === 'done' && publishUrl
                        ? () => navigator.clipboard.writeText(publishUrl)
                        : handlePublish
                    }
                  >
                    {publishStatus === 'done' ? 'Copy Link' : 'Share'}
                  </button>
                  {templateStatus === 'naming' ? (
                    <>
                      <input
                        style={styles.templateNameInput}
                        type="text"
                        value={templateName}
                        onChange={e => setTemplateName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && templateName.trim() && handleSaveTemplate()}
                        placeholder="Template name..."
                        autoFocus
                      />
                      <button
                        style={{ ...styles.publishButton, ...(templateName.trim() ? {} : styles.editButtonDisabled) }}
                        onClick={handleSaveTemplate}
                        disabled={!templateName.trim()}
                      >
                        Save
                      </button>
                      <button
                        style={styles.toolbarButton}
                        onClick={() => setTemplateStatus('idle')}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      style={templateStatus === 'saved' ? styles.publishedButton : styles.toolbarButton}
                      onClick={() => setTemplateStatus('naming')}
                      disabled={templateStatus === 'saving' || templateStatus === 'saved'}
                    >
                      {templateStatus === 'saving'
                        ? 'Saving...'
                        : templateStatus === 'saved'
                          ? 'Saved'
                          : 'Save as Template'}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div style={styles.body}>
        {/* Step log — only for live runs */}
        {isLiveRun && (
          <>
            <div
              style={styles.stepLogHeader}
              onClick={() => setStepsExpanded(prev => !prev)}
            >
              <span style={styles.stepLogToggle}>{stepsExpanded ? '\u25BC' : '\u25B6'}</span>
              <span style={styles.stepLogLabel}>
                Pipeline steps ({steps.length})
                {!stepsExpanded &&
                  steps.length > 0 &&
                  ` — ${steps.filter(s => s.status === 'complete').length} complete`}
              </span>
            </div>
            {stepsExpanded && (
              <div style={styles.stepLog}>
                {steps.map(s => (
                  <div
                    key={s.step}
                    style={styles.stepRow}
                  >
                    <span style={styles.stepIcon}>
                      {s.status === 'running' ? '\u23F3' : s.status === 'complete' ? '\u2713' : '\u2717'}
                    </span>
                    <span style={styles.stepName}>{s.step}</span>
                    {s.durationMs != null && <span style={styles.stepDuration}>{s.durationMs}ms</span>}
                    {s.error && <span style={styles.stepError}>{s.error}</span>}
                  </div>
                ))}
                {pipelineStatus === 'loading' && <div style={styles.waiting}>Loading run status...</div>}
                {pipelineStatus === 'running' && steps.length === 0 && (
                  <div style={styles.waiting}>Waiting for pipeline to start...</div>
                )}
              </div>
            )}
          </>
        )}

        {error && <div style={styles.error}>{error}</div>}

        {/* Edit bar — shown when output is ready and we have a folderName to edit */}
        {output && folderName && (
          <div style={styles.editBar}>
            <input
              style={styles.editInput}
              type="text"
              value={editInstruction}
              onChange={e => setEditInstruction(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !editSubmitting && handleEdit()}
              placeholder="Describe a change... (e.g. 'make the hero taller', 'change colors to blue')"
              disabled={editSubmitting}
            />
            <button
              style={{
                ...styles.editButton,
                ...(editSubmitting || !editInstruction.trim() ? styles.editButtonDisabled : {}),
              }}
              onClick={handleEdit}
              disabled={editSubmitting || !editInstruction.trim()}
            >
              {editSubmitting ? 'Starting...' : 'Edit'}
            </button>
          </div>
        )}

        {/* Loading state for history */}
        {!isLiveRun && !output && !error && <div style={styles.loading}>Loading...</div>}

        {/* Preview iframe — use SSR render endpoint when available, srcDoc as fallback */}
        {renderUrl && !contentOverride && (
          <iframe
            src={renderUrl}
            style={styles.iframe}
            title="Site Preview"
          />
        )}
        {previewHtml && (
          <iframe
            srcDoc={previewHtml}
            style={styles.iframe}
            title="Site Preview"
            sandbox="allow-scripts"
          />
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 16px',
    borderBottom: '1px solid #222',
    backgroundColor: '#0a0a0a',
  },
  toolbarTitle: { flex: 1, fontSize: 13, color: '#888' },
  toolbarActions: { display: 'flex', gap: 8 },
  toolbarButton: {
    padding: '4px 12px',
    backgroundColor: '#222',
    color: '#e0e0e0',
    border: '1px solid #333',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
  },
  downloadButton: {
    padding: '4px 12px',
    backgroundColor: '#fff',
    color: '#000',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  },
  publishButton: {
    padding: '4px 12px',
    backgroundColor: '#1a65d6',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  },
  publishedButton: {
    padding: '4px 12px',
    backgroundColor: '#1a8c3f',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  },
  hiddenInput: { display: 'none' },
  body: { display: 'flex', flexDirection: 'column' as const, height: 'calc(100vh - 45px)' },
  stepLogHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 24px',
    borderBottom: '1px solid #222',
    backgroundColor: '#0a0a0a',
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  stepLogToggle: { fontSize: 10, color: '#666', width: 12 },
  stepLogLabel: { fontSize: 12, color: '#666' },
  stepLog: { padding: '8px 24px 12px', borderBottom: '1px solid #222', backgroundColor: '#0a0a0a' },
  stepRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 0', color: '#e0e0e0' },
  stepIcon: { width: 16, textAlign: 'center' as const },
  stepName: { flex: 1, color: '#ccc' },
  stepDuration: { color: '#666', fontSize: 12 },
  stepError: { color: '#ff6b6b', fontSize: 12, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' },
  waiting: { fontSize: 13, color: '#666' },
  loading: { padding: 24, color: '#666', fontSize: 13 },
  error: {
    margin: 16,
    padding: 12,
    backgroundColor: 'rgba(255, 60, 60, 0.1)',
    border: '1px solid rgba(255, 60, 60, 0.3)',
    borderRadius: 8,
    color: '#ff6b6b',
    fontSize: 13,
  },
  editBar: {
    display: 'flex',
    gap: 8,
    padding: '8px 16px',
    borderBottom: '1px solid #222',
    backgroundColor: '#0a0a0a',
  },
  editInput: {
    flex: 1,
    padding: '6px 12px',
    backgroundColor: '#111',
    color: '#e0e0e0',
    border: '1px solid #333',
    borderRadius: 4,
    fontSize: 13,
    outline: 'none',
  },
  editButton: {
    padding: '6px 16px',
    backgroundColor: '#fff',
    color: '#000',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  },
  editButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  templateNameInput: {
    padding: '4px 10px',
    backgroundColor: '#111',
    color: '#e0e0e0',
    border: '1px solid #333',
    borderRadius: 4,
    fontSize: 13,
    width: 160,
    outline: 'none',
  },
  iframe: { flex: 1, width: '100%', border: 'none' },
};
